import type { TasProgramTemplateExtension as TasProgramTemplateModel } from 'tio-data/data/resources/tas-program-template.ext';
import type IntlService from 'ember-intl/services/intl';

export type CourseDateValidationResult = {
  date: Date;
  message: string;
};

export type FieldConfig = {
  visible?: boolean;
  required?: boolean;
};

/**
 * Normalize date to start of day in UTC to avoid timezone issues
 * Uses UTC methods to prevent timezone shifting
 */
function normalizeDate(date: Date): Date {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
}

/**
 * Parse ISO date string to normalized UTC date
 * For date-only strings (YYYY-MM-DD), creates UTC midnight to avoid timezone shifts
 */
function parseISODate(dateString: string): Date | null {
  if (!dateString) return null;

  // If it's a simple date string (YYYY-MM-DD), parse as UTC to avoid timezone issues
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    const parts = dateString.split('-').map(Number);
    const year = parts[0];
    const month = parts[1];
    const day = parts[2];

    if (year === undefined || month === undefined || day === undefined) {
      return null;
    }

    return new Date(Date.UTC(year, month - 1, day));
  }

  // Otherwise parse normally and normalize
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return null;
  return normalizeDate(date);
}

/**
 * Calculate the maximum allowed begin date based on program template settings
 */
export function getMaxBeginDate(
  template: TasProgramTemplateModel,
  intl?: IntlService
): CourseDateValidationResult {
  const maxLeadTime = template.coursesPreApprovalSubmissionMaxLeadTime;

  if (!maxLeadTime) {
    return {
      date: new Date('2099-01-01'),
      message: 'Begin date must be before Jan 1, 2099',
    };
  }

  const numberOfDays = Number(maxLeadTime);
  if (!numberOfDays) {
    return {
      date: new Date('2099-01-01'),
      message: 'Begin date must be before Jan 1, 2099',
    };
  }

  const today = normalizeDate(new Date());
  const maxDate = new Date(today.getTime() + numberOfDays * 24 * 60 * 60 * 1000);

  let formattedDate = '';
  if (intl) {
    formattedDate = intl.formatDate(maxDate, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      timeZone: 'UTC',
    });
  }

  return {
    date: maxDate,
    message: formattedDate
      ? `Your employer requires that you submit your application within ${numberOfDays} days of the start date. Change date to be on or before ${formattedDate}.`
      : `Your employer requires that you submit your application within ${numberOfDays} days of the start date.`,
  };
}

/**
 * Calculate the minimum allowed begin date based on program template settings
 */
export function getMinBeginDate(
  template: TasProgramTemplateModel,
  intl?: IntlService
): CourseDateValidationResult {
  let numberOfDays: number | null = null;
  let policyMessage = '';

  const { lastDayToSubmitApplicationForPreApproval, coursesPreApprovalSubmissionMinLeadTime } =
    template;

  if (lastDayToSubmitApplicationForPreApproval) {
    numberOfDays = -Number(lastDayToSubmitApplicationForPreApproval) || null;
    policyMessage = numberOfDays
      ? `Your employer requires that you submit your application within ${Math.abs(numberOfDays)} days after the start date.`
      : '';
  } else if (coursesPreApprovalSubmissionMinLeadTime) {
    numberOfDays = Number(coursesPreApprovalSubmissionMinLeadTime) || null;
    policyMessage = numberOfDays
      ? `Your employer requires that you submit your application at a minimum of ${numberOfDays} days before the start date.`
      : '';
  }

  if (!numberOfDays) {
    return {
      date: new Date('2000-01-01'),
      message: 'Begin date must be after Jan 1, 2000',
    };
  }

  const today = normalizeDate(new Date());
  const minDate = new Date(today.getTime() + numberOfDays * 24 * 60 * 60 * 1000);

  let formattedDate = '';
  if (intl) {
    formattedDate = intl.formatDate(minDate, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      timeZone: 'UTC',
    });
  }

  return {
    date: minDate,
    message:
      policyMessage + (formattedDate ? ` Change date to be on or after ${formattedDate}.` : ''),
  };
}

/**
 * Validate COURSES_BEGIN_DATE
 * Returns array of error messages (empty if valid)
 */
export function validateBeginDate(
  dateString: string,
  fieldConfig: FieldConfig,
  template: TasProgramTemplateModel,
  intl?: IntlService,
  employmentStartDate?: string,
  eligibilityWaitingPeriod?: number
): readonly string[] {
  // Don't validate if field is not visible
  if (!fieldConfig.visible) {
    return [];
  }

  // Check required
  if (fieldConfig.required && !dateString) {
    return ['Begin date is required'];
  }

  // If not required and empty, no validation needed
  if (!dateString) {
    return [];
  }

  const date = parseISODate(dateString);
  if (!date) {
    return ['Invalid date format'];
  }

  const errors: string[] = [];
  const maxBegin = getMaxBeginDate(template, intl);
  const minBegin = getMinBeginDate(template, intl);

  // Normalize for comparison
  const maxDate = normalizeDate(maxBegin.date);
  const minDate = normalizeDate(minBegin.date);

  if (date > maxDate) {
    errors.push(maxBegin.message);
  }

  if (date < minDate) {
    errors.push(minBegin.message);
  }

  // Check waiting period eligibility
  if (employmentStartDate && eligibilityWaitingPeriod) {
    const waitingPeriodDays = Number(eligibilityWaitingPeriod);
    if (waitingPeriodDays > 0) {
      const hireDate = parseISODate(employmentStartDate);
      if (hireDate) {
        // Calculate waiting period end date using setUTCDate to match service logic
        const waitingPeriodEndDate = new Date(hireDate);
        waitingPeriodEndDate.setUTCDate(waitingPeriodEndDate.getUTCDate() + waitingPeriodDays);
        const normalizedWaitingEndDate = normalizeDate(waitingPeriodEndDate);

        if (date < normalizedWaitingEndDate) {
          const formattedEndDate = intl
            ? intl.formatDate(normalizedWaitingEndDate, {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
                timeZone: 'UTC',
              })
            : normalizedWaitingEndDate.toISOString().split('T')[0];
          errors.push(
            `Your employer requires a ${waitingPeriodDays}-day waiting period from your hire date. Change date to be on or after ${formattedEndDate}.`
          );
        }
      }
    }
  }

  return errors;
}

/**
 * Validate COURSES_END_DATE
 * Returns array of error messages (empty if valid)
 */
export function validateEndDate(
  endDateString: string,
  beginDateString: string,
  fieldConfig: FieldConfig,
  isCertificateProgram: boolean
): readonly string[] {
  // Don't validate if field is not visible
  if (!fieldConfig.visible) {
    return [];
  }

  // Check required
  if (fieldConfig.required && !endDateString) {
    return ['End date is required'];
  }

  // If not required and empty, no validation needed
  if (!endDateString) {
    return [];
  }

  const endDate = parseISODate(endDateString);
  if (!endDate) {
    return ['Invalid date format'];
  }

  const errors: string[] = [];

  // For certificate programs, don't require end date to be after begin date
  // For regular programs, end date must be >= begin date (allow same-day courses)
  if (!isCertificateProgram && beginDateString) {
    const beginDate = parseISODate(beginDateString);
    if (beginDate && endDate < beginDate) {
      errors.push('End date must be on or after start date');
    }
  }

  const maxEndDate = normalizeDate(new Date('2099-01-01'));
  if (endDate > maxEndDate) {
    errors.push('End date must be before Jan 1, 2099');
  }

  return errors;
}
